import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Building2, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  RefreshCw,
  Check,
  X,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const WalletTab = ({ onPendingCountChange }) => {
  const [recharges, setRecharges] = useState([]);
  const [rechargesLoading, setRechargesLoading] = useState(false);
  const [rechargeFilter, setRechargeFilter] = useState('pending');

  const fetchRecharges = useCallback(async (filter = rechargeFilter) => {
    setRechargesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/wallet/recharges`, {
        params: { status: filter === 'all' ? '' : filter },
      });
      setRecharges(response.data.recharges || []);
      
      // Update pending count if callback provided
      if (onPendingCountChange && filter === 'pending') {
        onPendingCountChange((response.data.recharges || []).length);
      }
    } catch (error) {
      console.error('Error fetching recharges:', error);
      toast.error('Error al cargar las recargas');
    } finally {
      setRechargesLoading(false);
    }
  }, [rechargeFilter, onPendingCountChange]);

  useEffect(() => {
    fetchRecharges(rechargeFilter);
  }, [rechargeFilter, fetchRecharges]);

  const handleApproveRecharge = async (rechargeId) => {
    try {
      await axios.post(`${API}/admin/wallet/recharges/${rechargeId}/approve`, {});
      toast.success('Recarga aprobada correctamente');
      fetchRecharges(rechargeFilter);
    } catch (error) {
      console.error('Error approving recharge:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar la recarga');
    }
  };

  const handleRejectRecharge = async (rechargeId) => {
    if (!window.confirm('¿Estás seguro de rechazar esta recarga?')) return;
    try {
      await axios.post(`${API}/admin/wallet/recharges/${rechargeId}/reject`, {});
      toast.success('Recarga rechazada');
      fetchRecharges(rechargeFilter);
    } catch (error) {
      console.error('Error rejecting recharge:', error);
      toast.error(error.response?.data?.detail || 'Error al rechazar la recarga');
    }
  };

  return (
    <Card data-testid="wallet-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Gestion de Recargas
          </CardTitle>
          <div className="flex gap-2">
            <Select value={rechargeFilter} onValueChange={setRechargeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="approved">Aprobadas</SelectItem>
                <SelectItem value="rejected">Rechazadas</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => fetchRecharges(rechargeFilter)}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {rechargesLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : recharges.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No hay recargas {rechargeFilter === 'pending' ? 'pendientes' : ''}
          </p>
        ) : (
          <div className="space-y-3">
            {recharges.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  {r.method === 'bank_transfer' ? (
                    <Building2 className="h-8 w-8 text-blue-600" />
                  ) : (
                    <CreditCard className="h-8 w-8 text-blue-600" />
                  )}
                  <div>
                    <p className="font-medium">{r.user_email}</p>
                    <p className="text-sm text-muted-foreground">
                      {r.method === 'bank_transfer' ? `Transferencia - ${r.bank_selected}` : 'PayPal'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString('es-ES', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                    {r.transfer_reference && (
                      <p className="text-xs text-muted-foreground">Ref: {r.transfer_reference}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{r.amount.toFixed(2)} €</p>
                    <Badge className={
                      r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      r.status === 'approved' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }>
                      {r.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                      {r.status === 'approved' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                      {r.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                      {r.status === 'pending' ? 'Pendiente' : r.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                    </Badge>
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApproveRecharge(r.id)}>
                        <Check className="h-4 w-4 mr-1" /> Aprobar
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleRejectRecharge(r.id)}>
                        <X className="h-4 w-4 mr-1" /> Rechazar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WalletTab;
